import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { cn, videoAspectRatio } from "../../lib/utils";
import { CANVAS_IMAGE_HEIGHT, CANVAS_IMAGE_WIDTH } from "../../lib/videoUtils";
import type { PalmTouchPoints } from "../../types/palm";

const getGestureIcon = (gesture: number) => {
  const list = [
    "none",
    "pointing",
    "scissors",
    "three",
    "four",
    "",
    "fist",
    "thumbsUp",
    "l",
    "paper",
    "circle",
    "back",
    "shaka",
    "doubleL",
  ];
  return `/img/gesture/${list[gesture]}.svg`;
};

interface PalmVideoFrameProps {
  needGesture: number;
  status: string;
  adjustment: number;
  palmSide: number;
  scanSide: number;
  touchPoints: PalmTouchPoints | null;
  isGesture: boolean;
}
export const PalmVideoFrame = ({
  isGesture,
  needGesture,
  palmSide,
  scanSide,
  touchPoints,
}: PalmVideoFrameProps) => {
  const { t } = useTranslation();
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const getCamera = async () => {
    const videoElement = document.getElementById(
      "localVideo"
    ) as HTMLVideoElement;

    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter((d) => d.kind === "videoinput");

    const wide = cameras.find((c) => /wide|ultra/i.test(c.label));

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({
          video: {
            advanced: [
              {
                facingMode: { exact: "environment" }, // environment means rear camera
              },
            ],
            deviceId: wide?.deviceId,
          },
          audio: false,
        })
        .then(async (stream) => {
          streamRef.current = stream;
          if (videoElement) {
            videoElement.srcObject = stream;
          }
          const track = stream.getVideoTracks()[0];

          if (track) {
            track
              .applyConstraints({
                advanced: [
                  {
                    // @ts-expect-error-torch property is available on some devices but not in type definitions
                    torch: true,
                  },
                ],
              })
              .catch((err) =>
                console.error("Failed to turn on flashlight:", err)
              );
          }
        })
        .catch((error) => {
          console.error("error:", error);
          toast.error(t("errors.getCameraError"));
        });
    }
  };

  useEffect(() => {
    getCamera();
    return () => {
      // Stop the camera when the page is unloaded
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        videoRef.current?.pause();
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      }
    };
  }, []);

  // const showTip = () => {
  //   if (needGesture > 0) {
  //     return (
  //       <div className="self-center justify-start text-sm px-4 py-3 rounded-4 text-white shadow-[0px_6px_8px_0px_rgba(0,0,0,0.24)] bg-[#3B82F6]">
  //         show Gesture
  //       </div>
  //     );
  //   }

  //   if (adjustment) {
  //     const isKeepStill = adjustment === 23;
  //     return (
  //       <div
  //         className={cn(
  //           "self-center justify-start text-sm px-4 py-3 rounded-4 text-white shadow-[0px_6px_8px_0px_rgba(0,0,0,0.24)]",
  //           isKeepStill ? " bg-error" : " bg-[#3B82F6]"
  //         )}
  //       >
  //         {PalmAdjustmentMap[adjustment]}
  //       </div>
  //     );
  //   }

  //   return null;
  // };

  const targetTop = Number(
    (((touchPoints?.targetPoint.y ?? 0) / CANVAS_IMAGE_HEIGHT) * 100).toFixed(4)
  );
  const targetLeft = Number(
    (((touchPoints?.targetPoint.x ?? 0) / CANVAS_IMAGE_WIDTH) * 100).toFixed(4)
  );
  const thumbTop = Number(
    (((touchPoints?.thumbTip.y ?? 0) / CANVAS_IMAGE_HEIGHT) * 100).toFixed(4)
  );
  const thumbLeft = Number(
    (((touchPoints?.thumbTip.x ?? 0) / CANVAS_IMAGE_WIDTH) * 100).toFixed(4)
  );

  const showPoints = touchPoints != null && (thumbTop > 0 || targetTop > 0);
  // const showHandSquare =
  //   !showPoints && (palmSide === -1 || needGesture > 0) && !isGesture;
  const showHandSquare = false;
  const showHandOutline =
    (!showHandSquare && !isGesture && !showPoints) ||
    (isGesture && needGesture <= 0);

  return (
    <div
      className="relative w-full"
      style={{ aspectRatio: videoAspectRatio() }}
    >
      <video
        id="localVideo"
        ref={videoRef}
        autoPlay={true}
        controls={false}
        webkit-playsinline="true"
        playsInline={true}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      <canvas
        id="canvas"
        className="hidden"
        width={videoRef.current?.videoWidth || 1280}
        height={videoRef.current?.videoHeight || 960}
      />

      <div className="absolute w-full h-full flex flex-col justify-between items-center pt-10 pb-5">
        <div
          className="w-full flex justify-center items-center "
          style={{ aspectRatio: "1/1" }}
        >
          {isGesture && needGesture > 0 && (
            <img
              src={getGestureIcon(needGesture)}
              className={cn(
                "w-full h-auto",
                (palmSide === 1 || scanSide === 1) && "scale-x-[-1]"
              )}
            />
          )}
          {showHandOutline && (
            <img
              src="/img/palm/right-hand-mask.svg"
              className={cn(
                "mx-10 w-full h-full object-fit",
                !showHandSquare && palmSide === 0 && "scale-x-[-1]"
              )}
            />
          )}

          {showHandSquare && (
            <img
              src="/img/palm/hand-outline-both.svg"
              className="absolute top-0 left-0 w-full h-full "
            />
          )}
        </div>

        {/* {isGesture && showTip()} */}
      </div>

      {showPoints && !isGesture && (
        <>
          <div
            className={cn("absolute rounded-full ")}
            style={{
              top: `${thumbTop}%`,
              left: `${thumbLeft}%`,
              transform: "translate(-50%, -50%)",
            }}
          >
            <img src="/svg/dot.svg" className="w-8 h-8" />
          </div>
          <div
            className={cn("absolute rounded-full ")}
            style={{
              top: `${targetTop}%`,
              left: `${targetLeft}%`,
              // transform: "translate(-50%, -50%)",
            }}
          >
            <img src="/svg/dot.svg" className="w-8 h-8" />
          </div>
        </>
      )}
    </div>
  );
};
